import { useMutation } from '@apollo/client';
import React, { useContext, useRef, useState } from 'react';
import { UserContext } from '../../../../../context/UserContext';
import { CreateFilesResponse, CreateFilesVariables, FileMutations } from '../../../../../graphql/file.graphql';
import { UpdateUserResponse, UpdateUserVariables, UserMutations } from '../../../../../graphql/user.graphql';
import { useToast } from '../../../../../providers/ToastProvider';
import { LanguageUtils } from '../../../../../types/common/languageRegionDesignator';
import { SystemCompany } from '../../../../../types/company';
import { CreateFileInput, FileEntityType } from '../../../../../types/file';
import { TranslationKey } from '../../../../../types/translation/TranslationKey';
import { t } from '../../../../../types/translation/Translator';
import { User } from '../../../../../types/user';
import { UserInput } from '../../../../../types/user/user.input';
import { Button } from '../../../../components/Button';
import { Dropdown, DropdownList, LanguageSelect, LocaleSelect } from '../../../../components/Dropdown';
import { LoadingSpinner } from '../../../../components/LoadingSpinner';
import { ProfileAvatar } from '../../../../components/Profile';
import { Surface } from '../../../../components/Surface';
import { TextInput } from '../../../../components/TextInput';
import { ToastType } from '../../../../components/Toast';
import Icon, { IconType } from '../../../../icons/Icon';

interface ProfileSettingsDetailsViewProps {
  user: User;
}

export default function ProfileSettingsDetailsView({ user }: ProfileSettingsDetailsViewProps) {
  const { signOut, setCurrentUser } = useContext(UserContext);
  const { generateId, showToast, closeToast } = useToast();

  const [input, setInput] = useState(new UserInput(user));

  const [update, { loading }] = useMutation<UpdateUserResponse, UpdateUserVariables>(UserMutations.update, {
    variables: {
      user: input.forUpdate(),
    },
    onCompleted: response => {
      const updatedUser = new User(response.updateUser);
      if (updatedUser.language !== user.language) {
        const toastId = generateId();
        showToast(
          {
            id: toastId,
            type: ToastType.success,
            title: 'Profile information updated successfully!',
            content: (
              <p className='text-sm text-gray-600'>
                Your language was updated! To make sure all changes take effect please sign out and sign in again.{' '}
                <span className='text-blue-600'>
                  <button
                    onClick={() => {
                      signOut();
                      closeToast(toastId);
                    }}
                  >
                    Click here
                  </button>
                </span>{' '}
                to sign out.
              </p>
            ),
          },
          10000,
        );
      } else {
        showToast({ type: ToastType.success, title: 'Profile information updated successfully' });
      }
      setCurrentUser(updatedUser);
      setInput(new UserInput(updatedUser));
    },
  });

  const handleSave = () => {
    update();
  };

  return (
    <Surface>
      <div className='grid gap-4'>
        <div className='flex items-center gap-4'>
          <div className='h-24 w-24'>
            <ProfileAvatarUpdateElement user={input} onChange={fileId => setInput(input.withProfilePicture(fileId))} />
          </div>
          <div>
            <p className='section-title'>{input.fullName}</p>
            <p className='subtitle'>{input.email}</p>
          </div>
        </div>
        <div className='r-grid'>
          <TextInput
            value={input.firstName}
            onChange={value => setInput(input.withFirstName(value))}
            label={t(TranslationKey.firstName).singular.upper}
          />
          <TextInput
            value={input.lastName}
            onChange={value => setInput(input.withLastName(value))}
            label={t(TranslationKey.lastName).singular.upper}
          />
        </div>
        <div className='r-grid'>
          <LanguageSelect
            label
            language={LanguageUtils.translationLanguageFromUser(input)}
            setLanguage={language => setInput(input => input.withLanguage(language))}
          />
          <LocaleSelect locale={input.locale} setLocale={locale => setInput(input => input.withLocale(locale))} />
        </div>
        <Button
          className='w-fit'
          text={t(TranslationKey.saveChanges).singular.upper}
          onClick={handleSave}
          loading={loading}
          disabled={!input.validate() || !input.hasChanges(user)}
        />
      </div>
    </Surface>
  );
}

interface ProfileAvaterUpdateElement {
  user: User;
  onChange: (fileId: string | undefined) => void;
}

function ProfileAvatarUpdateElement({ user, onChange }: ProfileAvaterUpdateElement) {
  const ref = useRef<HTMLInputElement>(null);
  const [hovered, setHovered] = useState(false);
  const [open, setOpen] = useState(false);

  const [create, { loading }] = useMutation<CreateFilesResponse, CreateFilesVariables>(FileMutations.create, {
    onCompleted: response => {
      onChange(response.createFiles[0].id);
      setOpen(false);
    },
  });

  const handleInputChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.currentTarget.files?.length) {
      const input = await CreateFileInput.fromFile(
        event.currentTarget.files[0],
        user.id,
        FileEntityType.user,
        SystemCompany.id,
      );
      await create({ variables: { files: [input] } });
      if (event.currentTarget) event.currentTarget.value = '';
    }
  };

  const openFilePicker = () => {
    if (ref && ref.current) ref.current.click();
  };

  return (
    <Dropdown
      width={'200px'}
      open={open}
      setOpen={setOpen}
      dropdown={
        <DropdownList
          items={Object.values(ProfileAvatarMenuOption)}
          textBuilder={item => {
            switch (item) {
              case ProfileAvatarMenuOption.remove:
                return 'Remove profile picture';
              case ProfileAvatarMenuOption.update:
                return 'Update profile picture';
            }
          }}
          onClick={item => {
            setOpen(false);
            switch (item) {
              case ProfileAvatarMenuOption.remove:
                return onChange(undefined);
              case ProfileAvatarMenuOption.update:
                return openFilePicker();
            }
          }}
        />
      }
    >
      <figure
        className={`relative flex items-center justify-center`}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
      >
        <input
          disabled={loading}
          ref={ref}
          type='file'
          className='hidden'
          onChange={handleInputChange}
          accept='.png,.jpg,.jpeg,.svg'
        />

        <div className={hovered || loading ? 'opacity-50' : ''}>
          <ProfileAvatar
            user={user}
            size={'lg'}
            onClick={() => (user.profilePicture ? setOpen(!open) : openFilePicker())}
          />
        </div>
        <figcaption
          className={`pointer-events-none absolute z-10 ${hovered || loading ? 'visible' : 'hidden'} stroke-black`}
        >
          {loading ? <LoadingSpinner /> : <Icon type={IconType.camera1} size={'h-6 w-6'} />}
        </figcaption>
      </figure>
    </Dropdown>
  );
}

enum ProfileAvatarMenuOption {
  update = 'update',
  remove = 'remove',
}
